import { useEffect, useRef, useState } from "react";

/**
 * Live content width of an element, measured with a ResizeObserver.
 *
 * The house rule (#11426, #11742): content-surface breakpoints key on the
 * *frame* the content actually has — never the viewport — because the nav
 * rail, split panes and overlays all change available width without a
 * resize. `frameWidth` is null until the first measurement lands (one frame
 * after mount); callers choose their own unmeasured fallback — the
 * CollectionToolbar renders the wide layout, the Name-column cap falls back
 * to the viewport.
 *
 * `frameRef` is a callback ref: the observer is (re)attached from whatever
 * the ref is handed, so a surface whose framed element only mounts after an
 * early return (a skeleton, an empty state) is measured once it appears —
 * the pinned case (#11742 round 7). A consumer that moves the ref between
 * two mounted elements relies on the same mechanism but is not pinned by a
 * test today.
 */
type FrameRef<T> = ((node: T | null) => void) & { current: T | null };

export function useFrameWidth<T extends HTMLElement>(): {
  frameRef: FrameRef<T>;
  frameWidth: number | null;
} {
  const [el, setEl] = useState<T | null>(null);
  const [frameWidth, setFrameWidth] = useState<number | null>(null);
  // Callable for `ref=`, and it keeps `.current` so a caller that reads the
  // element directly (the toolbar's portal-root lookup) still can. Held in
  // a ref, not a memo: the callback's identity must be stable, and useMemo
  // only promises to try.
  const frameRefHolder = useRef<FrameRef<T> | null>(null);
  if (frameRefHolder.current === null) {
    const ref = ((node: T | null) => {
      ref.current = node;
      setEl(node);
    }) as FrameRef<T>;
    ref.current = null;
    frameRefHolder.current = ref;
  }
  const frameRef = frameRefHolder.current;
  useEffect(() => {
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setFrameWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return { frameRef, frameWidth };
}
